import { useCallback, useMemo } from 'react';
import { useListKadryStaffQuery, useMeQuery } from '@shared/api';
import type { KadryStaffMember } from './types';
import { useKadryAccess } from './useKadryAccess';

export interface KadryStaffDirectory {
  /** Справочник доступен роли: только назначающим (рекрутер, администратор подбора). */
  available: boolean;
  recruiters: KadryStaffMember[];
  requesters: KadryStaffMember[];
  byId: Map<string, KadryStaffMember>;
  isLoading: boolean;
  /**
   * ФИО сотрудника: из справочника или «вы» (текущий пользователь). `undefined`
   * — имя неизвестно этой роли; показывать полный UUID (`KadryPerson`).
   */
  nameOf: (userId: string | null | undefined) => string | undefined;
}

/**
 * Справочник сотрудников Кадров (рекрутеры и заказчики) для назначения на
 * заявку, выбора заказчика и подписей «кто сделал». Сервер отдаёт его только
 * рекрутеру и администратору подбора; остальным известно лишь собственное имя.
 */
export function useKadryStaff(): KadryStaffDirectory {
  const { ready, assigns } = useKadryAccess();
  const me = useMeQuery().data?.user;
  const available = ready && assigns;
  const rec = useListKadryStaffQuery('recruiter', { skip: !available });
  const req = useListKadryStaffQuery('requester', { skip: !available });

  const byId = useMemo(() => {
    const m = new Map<string, KadryStaffMember>();
    for (const s of [...(rec.data ?? []), ...(req.data ?? [])]) m.set(s.user_id, s);
    return m;
  }, [rec.data, req.data]);

  const nameOf = useCallback(
    (userId: string | null | undefined) => {
      if (!userId) return undefined;
      const known = byId.get(userId)?.full_name;
      if (known) return known;
      if (me && me.id === userId) return me.full_name;
      return undefined;
    },
    [byId, me],
  );

  return {
    available,
    recruiters: rec.data ?? [],
    requesters: req.data ?? [],
    byId,
    isLoading: rec.isLoading || req.isLoading,
    nameOf,
  };
}
